import { authApi } from "@/api/authApi";
import { TokenStorage } from "@/utils/tokenStorage";
import { CONFIG } from "@/config";
import { deleteAccountLog, resetPasswordLog, signInLog, signOutLog, signUpLog } from "@/firebase/events";

export enum AuthEvent {
  SIGN_IN = "SIGN_IN",
  SIGN_OUT = "SIGN_OUT",
  SIGN_UP = "SIGN_UP",
  REFRESH = "REFRESH",
  RESET_PASSWORD = "RESET_PASSWORD",
  REFRESH_FAILED = "REFRESH_FAILED",
}

function handleAxiosError(error: any) {
  if (error.response && error.response.data && error.response.data.code) {
    console.log(error.response.data.code);
    return error.response.data.code;
  }
  throw error;
}

export interface AuthServiceInterface {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, birthDate: string) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
  isAuthenticated: boolean|null;
} 

class AuthService implements AuthServiceInterface {

  isAuthenticated:boolean|null = null;
  
  private listeners: { [K in AuthEvent]?: Array<() => void> } = {};
  private refreshPromise: Promise<boolean> | null = null;
  private isRefreshingInProgress: boolean = false;

  constructor() {
    // Инициализируем состояние из localStorage при создании сервиса
    this.initializeFromStorage();
    
    // Добавляем слушатель событий localStorage для синхронизации между вкладками
    this.setupStorageListener();
  }

  private initializeFromStorage() {
    try {
      if (TokenStorage.isTokenValid()) {
        this.isAuthenticated = true;
      } else {
        this.isAuthenticated = false;
        TokenStorage.clearAuthState();
      }
    } catch (error) {
      console.log("Ошибка при инициализации из localStorage:", error);
      TokenStorage.clearAuthState();
    }
  }

  private setupStorageListener() {
    try {
      window.addEventListener('storage', (event) => {
        if (event.key === 'horoscope_auth_state' || event.key === 'horoscope_session_id') {
          console.log('Обнаружено изменение в localStorage, обновляем состояние аутентификации');
          
          // Если токен был очищен в другой вкладке, разлогиниваем пользователя
          if (event.key === 'horoscope_auth_state' && !event.newValue) {
            this.isAuthenticated = false;
            this.emit(AuthEvent.SIGN_OUT);
          }
          
          // Если сессия была сброшена, разлогиниваем пользователя
          if (event.key === 'horoscope_session_id' && !event.newValue) {
            this.isAuthenticated = false;
            TokenStorage.clearAuthState();
            this.emit(AuthEvent.SIGN_OUT);
          }
        }
      });
    } catch (error) {
      console.log("Ошибка при настройке слушателя localStorage:", error);
    }
  }

  on(event: AuthEvent, callback: () => void) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event]!.push(callback);
  }

  off(event: AuthEvent, callback: () => void) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event]!.filter(
      (cb) => cb !== callback
    );
  }

  private emit(event: AuthEvent) {
    this.listeners[event]?.forEach((cb) => cb());
  }

  async signIn(email: string, password: string) {
    try {
      await authApi.signIn(email, password);
      this.isAuthenticated = true;
      TokenStorage.saveAuthState(true);
      this.emit(AuthEvent.SIGN_IN);
      await signInLog();
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }

  async signUp( 
    email: string,
    password: string,
    name: string,
    birthDate: string
  ) {
    try {
      await authApi.signUp(email, password, name, birthDate);
      this.isAuthenticated = true;
      TokenStorage.saveAuthState(true);
      this.emit(AuthEvent.SIGN_UP);
      await signUpLog();
    } catch (error: any) {
      console.log(error);
      return handleAxiosError(error);
    }
  }

  async refreshToken() {
    // Если обновление уже в процессе, возвращаем существующий промис
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Если обновление уже запущено, но промис еще не создан, ждем немного
    if (this.isRefreshingInProgress) {
      // Ждем небольшое время и проверяем снова
      await new Promise(resolve => setTimeout(resolve, 100));
      if (this.refreshPromise) {
        return this.refreshPromise;
      }
    }

    this.isRefreshingInProgress = true;
    this.refreshPromise = this.performRefreshToken();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
      this.isRefreshingInProgress = false;
    }
  }

  private async performRefreshToken(): Promise<boolean> {
    try {
      await authApi.refreshToken();
      this.isAuthenticated = true;
      TokenStorage.saveAuthState(true);
      this.emit(AuthEvent.REFRESH);
      return true;
    } catch (error) {
      console.log("Ошибка при обновлении токена:", error);
      // Не разлогиниваем пользователя сразу при ошибке обновления токена
      // Позволяем axios interceptor попробовать еще раз
      if (this.isAuthenticated === true) {
        // Если пользователь был аутентифицирован, не меняем статус
        // Это предотвратит разлогинивание при временных ошибках
        return true;
      }
      this.isAuthenticated = false;
      TokenStorage.clearAuthState();
      this.emit(AuthEvent.REFRESH_FAILED);
      return false;
    }
  }

  async signOut() {
    try {
      await signOutLog();
      await authApi.signOut();
    } catch (error: any) {
      return handleAxiosError(error);
    } finally {
      this.isAuthenticated = false;
      TokenStorage.forceLogout(); // Используем forceLogout для полной очистки
      this.emit(AuthEvent.SIGN_OUT);
      
    }
  }

  async sendResetCode(email: string) {
    try {
      await authApi.sendResetCode(email);
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }

  async resetPassword(email: string, code: string, password: string) {
    try {
      await resetPasswordLog();
      await authApi.resetPassword(email, code, password);
      this.emit(AuthEvent.RESET_PASSWORD);
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }

  async deleteAccount() {
    try {
      await deleteAccountLog();
      await authApi.deleteAccount();
      this.isAuthenticated = false;
      TokenStorage.forceLogout();
      this.emit(AuthEvent.SIGN_OUT);
      
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }

  async initialize() {
    try {
      // Сначала проверяем localStorage
      if (this.isAuthenticated === true && TokenStorage.isTokenValid()) {
        // Если токен еще действителен, проверяем его актуальность на сервере
        // Делаем легкую проверку без обновления токена
        try {
          // Проверяем, что токен действительно валиден на сервере
          await this.validateTokenOnServer();
          return; // Токен валиден, не делаем запрос на обновление
        } catch (error) {
          // Токен не валиден на сервере, очищаем localStorage
          console.log("Токен в localStorage не валиден на сервере:", error);
          this.isAuthenticated = false;
          TokenStorage.clearAuthState();
        }
      }
      
      const isRefreshed = await this.refreshToken();
      this.isAuthenticated = isRefreshed;
    } catch (error) {
      console.log("Ошибка при инициализации аутентификации:", error);
      this.isAuthenticated = false;
      TokenStorage.clearAuthState();
    }
  }

  private async validateTokenOnServer(): Promise<boolean> {
    try {
      // Делаем легкий запрос для проверки валидности токена
      // Например, запрос на получение профиля пользователя
      // Если запрос проходит успешно, токен валиден
      // Если получаем 401, токен не валиден
      
      // Используем clientFree для избежания автоматического обновления токена
      const response = await fetch(`${CONFIG.api.baseUrl}/v2/users/profile?isCookie=true`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        return true; // Токен валиден
      } else if (response.status === 401) {
        return false; // Токен не валиден
      } else {
        // Другие ошибки - считаем токен валидным, чтобы не разлогинивать пользователя
        return true;
      }
    } catch (error) {
      console.log("Ошибка при проверке токена на сервере:", error);
      // При ошибке сети считаем токен валидным, чтобы не разлогинивать пользователя
      return true;
    }
  }

}

export const authService = new AuthService();



